import { App } from 'antd'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { downloadFile, useShowApiErrors } from 'src/utils'

import { opensInline } from './attachments'
import type { ChatAttachment } from './attachments'

export type ResolveAttachmentUrl = (
  attachment: ChatAttachment
) => Promise<string>

// Without `target`, so the click is a plain navigation that Safari allows
// after an await; the attachment disposition keeps the page where it is.
const saveInPlace = (url: string, filename: string) => {
  const link = document.createElement('a')

  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
}

/**
 * Opens a file through a fresh presigned link. Account has no document
 * preview: Squad serves images and PDFs inline (the browser shows them in a
 * new tab) and everything else as a download.
 *
 * Safari blocks `_blank` navigations that follow an await, so an inline file
 * gets its tab opened in the click handler and pointed at the link once it
 * resolves. A download needs no tab and is saved in place.
 */
export const useAttachmentActions = (resolveUrl?: ResolveAttachmentUrl) => {
  const { message } = App.useApp()
  const { t } = useTranslation()
  const showApiErrors = useShowApiErrors()
  const [busyId, setBusyId] = useState<string | null>(null)
  const latestRequest = useRef(0)

  const run = async (attachment: ChatAttachment) => {
    latestRequest.current += 1

    const request = latestRequest.current
    const isLatestRequest = () => latestRequest.current === request

    setBusyId(attachment.id)

    const tab = opensInline(attachment.mimeType)
      ? window.open('', '_blank')
      : null

    try {
      const url = resolveUrl ? await resolveUrl(attachment) : attachment.url

      if (!isLatestRequest() || !url) {
        tab?.close()

        if (isLatestRequest()) {
          void message.warning(t('workspaceHome.attachments.unavailable'))
        }

        return
      }

      if (tab) {
        tab.opener = null
        tab.location.href = url
      } else if (opensInline(attachment.mimeType)) {
        downloadFile(url, attachment.filename)
      } else {
        saveInPlace(url, attachment.filename)
      }
    } catch (error) {
      tab?.close()

      if (isLatestRequest()) showApiErrors(error)
    } finally {
      if (isLatestRequest()) setBusyId(null)
    }
  }

  return {
    busyId,
    open: (attachment: ChatAttachment) => void run(attachment)
  }
}
